<template>
  <div class="flex h-full flex-col">
    <ClientOnly>
      <div v-show="isOpen" class="relative flex-1">
        <div
          id="rx-fitmix-container"
          ref="rx-fitmix-container"
          class="relative aspect-[1920/1075] size-full"
        ></div>
        <button
          data-testid="close-button"
          class="absolute right-4 top-4 z-10 cursor-pointer p-2"
          :aria-label="$t('modal.close')"
          @click="onClose"
        >
          <LazyIconFielmannClose class="size-6" />
        </button>
      </div>
    </ClientOnly>

    <div class="absolute inset-x-0 bottom-4 flex justify-center md:bottom-16">
      <FimButton
        v-if="!isOpen"
        type="secondary"
        :disabled="!fitMixReady"
        data-testid="virtual-try-on-button-rx"
        @click="openVirtualWindow"
      >
        <template #icon>
          <LazyIconFielmannWebcam class="size-4 md:size-6" />
        </template>
        {{ $t('vto.button_label') }}
      </FimButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { Product, Variant } from '@scayle/storefront-nuxt'
import type {
  FitMixInstance,
  FitMixLensMaterial,
  FitMixParameters,
} from '~/types/fitmix'

const props = defineProps({
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  variant: {
    type: Object as PropType<Variant>,
    required: true,
  },
  isOpen: {
    type: Boolean as PropType<boolean>,
    required: true,
  },
  lensColor: {
    type: String as PropType<string>,
    default: undefined,
  },
})

const emit = defineEmits<{
  (
    e: 'onVtoAction' | 'onVtoChangeFrame',
    value: { product: Product; variant: Variant | undefined },
  ): void
  (e: 'onVtoClose' | 'onVtoOpen'): void
}>()

const instance = useNuxtApp()
const { $currentShop, $fittingBox } = instance
const { trackVTOFittingBoxOpen, trackVTOFittingBoxClose } =
  await useTrackingEvents()

// VTO setup
const container = useTemplateRef<HTMLDivElement>('rx-fitmix-container')
const scriptReady = computed(() => $fittingBox.scriptReady.value)
const containerReady = ref(false)
const fitMixReady = ref(false)
const fitMixCreated = ref(false)
const fitmixInstance = ref<FitMixInstance>()

const activeEan = computed(() =>
  props.variant ? getVirtualTryOnEan(props.variant) : undefined,
)

const vtoApiKey = computed(() => $fittingBox.vtoApiKey.value)

const initialize = () => {
  if (import.meta.server) {
    return
  }

  if (!scriptReady.value) {
    return
  }

  if (!vtoApiKey.value) {
    return
  }

  if (!activeEan.value) {
    return
  }

  if (!container.value) {
    return
  }

  const params: FitMixParameters = {
    apiKey: vtoApiKey.value,
    lang: getIsoLanguage(toValue($currentShop).locale),
    frame: activeEan.value,
    uiConfiguration: {
      apiKeyIssueMessage: true,
      frameIssueMessage: true,
      liveCameraAccessDenied: true,
      liveDetectionFailure: true,
      liveLensesAnimations: true,
      liveLensesAnimationsActive: true,
      livePhotoButton: false,
      liveRelaunchDetectionButton: false,
      loadingIndicator: true,
      networkIssueMessage: true,
      photoLiveButton: false,
      photoRenderError: false,
      photoWelcomeScreen: true,
    },
    onStopVto: () => {
      emit('onVtoClose')
    },
  }

  try {
    fitMixCreated.value = true
    fitmixInstance.value = $fittingBox.createFitMixWidget(
      container.value.id,
      params,
      (ready) => {
        fitMixReady.value = ready
      },
    )
  } catch (error) {
    console.error('VTO: Error creating widget:', error)
    fitMixCreated.value = false
    fitMixReady.value = false
  }
}

const onClose = () => {
  fitmixInstance.value?.resetSession()
  fitmixInstance.value?.stopVto()

  if (props.variant) {
    trackVTOFittingBoxClose({
      productDetails: props.product,
      variant: props.variant,
      element: { id: 'close-button', label: 'Schließen' },
    })
  }
}

const openVirtualWindow = (event: Event) => {
  emit('onVtoOpen')

  if (props.variant) {
    trackVTOFittingBoxOpen({
      productDetails: props.product,
      variant: props.variant,
      element: event?.target as HTMLButtonElement,
    })
  }

  if (fitmixInstance.value && activeEan.value) {
    fitmixInstance.value.setFrame(activeEan.value)
    fitmixInstance.value.startVto('live')
  }
}

const scope = effectScope()
onScopeDispose(() => scope.stop())

scope.run(() => {
  if (import.meta.server) {
    return
  }

  watch(
    container,
    (newContainer) => {
      containerReady.value = Boolean(newContainer)
      if (containerReady.value && scriptReady.value && !fitMixCreated.value) {
        initialize()
      }
    },
    { immediate: true },
  )

  watch(
    scriptReady,
    (ready) => {
      if (ready && containerReady.value && !fitMixCreated.value) {
        initialize()
      }
    },
    { immediate: true },
  )
})

onBeforeUnmount(() => {
  $fittingBox.resetSession('rx-fitmix-container')
})

watch(
  () => props.lensColor,
  (newColor) => {
    if (fitmixInstance.value) {
      if (newColor === undefined) {
        fitmixInstance.value.restoreLensDefaultMaterial()
      } else {
        fitmixInstance.value.setLensMaterial(newColor as FitMixLensMaterial)
      }
    }
  },
)
</script>
